import { FetchResult, useApolloClient } from "@apollo/client"
import { Button } from "@chakra-ui/react"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import useControlledState from "@pathwright/ui/src/components/hooks/useControlledState"
import { useColorModeValue } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { exhaustiveGuard } from "@pathwright/ux/src/utils/typing"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useHistory, useRouteMatch } from "react-router-dom"
import styled from "styled-components"
import {
  CreateCurriculumLicenseOrderDocument,
  CreateCurriculumLicenseOrderMutation,
  CreateCurriculumLicenseOrderMutationVariables,
  CreateCurriculumSubscriptionOrderDocument,
  CreateCurriculumSubscriptionOrderMutation,
  CreateCurriculumSubscriptionOrderMutationVariables,
  CreateGiftSubscriptionOrderDocument,
  CreateGiftSubscriptionOrderMutation,
  CreateGiftSubscriptionOrderMutationVariables,
  CreateGroupLicenseOrderDocument,
  CreateGroupLicenseOrderMutation,
  CreateGroupLicenseOrderMutationVariables,
  CreateGroupSubscriptionOrderDocument,
  CreateGroupSubscriptionOrderMutation,
  CreateGroupSubscriptionOrderMutationVariables,
  CreateLifetimeSchoolSubscriptionOrderDocument,
  CreateLifetimeSchoolSubscriptionOrderMutation,
  CreateLifetimeSchoolSubscriptionOrderMutationVariables,
  CreatePlatformSubscriptionOrderDocument,
  CreatePlatformSubscriptionOrderMutation,
  CreatePlatformSubscriptionOrderMutationVariables,
  CreateRegistrationOrderDocument,
  CreateRegistrationOrderMutation,
  CreateRegistrationOrderMutationVariables,
  CreateSchoolSubscriptionOrderDocument,
  CreateSchoolSubscriptionOrderMutation,
  CreateSchoolSubscriptionOrderMutationVariables,
  OrderFragment,
  OrderStripeCheckoutRedirects,
  OrderType,
  useContextLazyQuery
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { useSignupEventTracking } from "../plausible/useSignupEventTracking"
import { ORDER_CREATED_EVENT } from "../utils/dispatcher"
import { getRelativeUrl } from "../utils/urls"
import usePendingPlatformOrderPolling from "./usePendingPlatformOrderPolling"
import { dispatchOrderEvent } from "./utils/order-events"
import { getStripeCheckoutRedirects } from "./utils/stripe-checkout-redirects"

const CenteredContent = styled.div`
  padding: 10px 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
`

export function useStripeCheckoutRedirects(): OrderStripeCheckoutRedirects {
  const { url } = useRouteMatch()

  return useMemo(() => getStripeCheckoutRedirects(url), [url])
}

type UseOrderStripeCheckoutProps = (
  | {
      orderType: OrderType.CurriculumLicense
      orderData: CreateCurriculumLicenseOrderMutationVariables
    }
  | {
      orderType: OrderType.CurriculumSubscription
      orderData: CreateCurriculumSubscriptionOrderMutationVariables
    }
  | {
      orderType: OrderType.GiftSubscription
      orderData: CreateGiftSubscriptionOrderMutationVariables
    }
  | {
      orderType: OrderType.GroupLicense
      orderData: CreateGroupLicenseOrderMutationVariables
    }
  | {
      orderType: OrderType.LifetimeSchoolSubscription
      orderData: CreateLifetimeSchoolSubscriptionOrderMutationVariables
    }
  | {
      orderType: OrderType.Registration
      orderData: CreateRegistrationOrderMutationVariables
    }
  | {
      orderType: OrderType.SchoolSubscription
      orderData: CreateSchoolSubscriptionOrderMutationVariables
    }
  | {
      orderType: OrderType.PlatformSubscription
      orderData: CreatePlatformSubscriptionOrderMutationVariables
    }
  | {
      orderType: OrderType.GroupSubscription
      orderData: CreateGroupSubscriptionOrderMutationVariables
    }
) & {
  stripeCheckoutRedirects?: OrderStripeCheckoutRedirects
}

export const useOrderStripeCheckout = ({
  orderType,
  orderData,
  stripeCheckoutRedirects: stripeCheckoutRedirectsProp
}: UseOrderStripeCheckoutProps) => {
  const apolloClient = useApolloClient()
  const { me } = usePathwrightContext()
  const { savePendingActivateContext } = useSignupEventTracking()

  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [order, setOrder] = useState<OrderFragment | null>(null)
  const clearError = useCallback(() => setError(null), [])
  const localStripeCheckoutRedirects = useStripeCheckoutRedirects()
  const stripeCheckoutRedirects =
    stripeCheckoutRedirectsProp ?? localStripeCheckoutRedirects

  const handleOrder = useCallback(async () => {
    try {
      setError(null)
      setLoading(true)

      function getMutationResult(): Promise<
        FetchResult<
          | CreateCurriculumLicenseOrderMutation
          | CreateCurriculumSubscriptionOrderMutation
          | CreateGiftSubscriptionOrderMutation
          | CreateGroupLicenseOrderMutation
          | CreateLifetimeSchoolSubscriptionOrderMutation
          | CreateRegistrationOrderMutation
          | CreateSchoolSubscriptionOrderMutation
          | CreatePlatformSubscriptionOrderMutation
          | CreateGroupSubscriptionOrderMutation
        >
      > {
        switch (orderType) {
          case OrderType.CurriculumLicense:
            return apolloClient.mutate<
              CreateCurriculumLicenseOrderMutation,
              CreateCurriculumLicenseOrderMutationVariables
            >({
              mutation: CreateCurriculumLicenseOrderDocument,
              variables: {
                license_offering_id: orderData.license_offering_id,
                licensee_school_id: orderData.licensee_school_id,
                licensor_school_id: orderData.licensor_school_id,
                name: orderData.name,
                seat_count: orderData.seat_count,
                role: orderData.role,
                coupon_code: orderData.coupon_code,
                stripe_checkout_redirects: stripeCheckoutRedirects
              }
            })

          case OrderType.CurriculumSubscription:
            return apolloClient.mutate<
              CreateCurriculumSubscriptionOrderMutation,
              CreateCurriculumSubscriptionOrderMutationVariables
            >({
              mutation: CreateCurriculumSubscriptionOrderDocument,
              variables: {
                curriculum_plan_id: orderData.curriculum_plan_id,
                plan_visibility_code: orderData.plan_visibility_code,
                licensee_id: orderData.licensee_id,
                stripe_checkout_redirects: stripeCheckoutRedirects
              }
            })

          case OrderType.GiftSubscription:
            return apolloClient.mutate<
              CreateGiftSubscriptionOrderMutation,
              CreateGiftSubscriptionOrderMutationVariables
            >({
              mutation: CreateGiftSubscriptionOrderDocument,
              variables: {
                plan_id: orderData.plan_id,
                plan_visibility_code: orderData.plan_visibility_code,
                num_cycles: orderData.num_cycles,
                recipient_first_name: orderData.recipient_first_name,
                recipient_last_name: orderData.recipient_last_name,
                gift_message: orderData.gift_message,
                stripe_checkout_redirects: stripeCheckoutRedirects
              }
            })

          case OrderType.GroupLicense:
            return apolloClient.mutate<
              CreateGroupLicenseOrderMutation,
              CreateGroupLicenseOrderMutationVariables
            >({
              mutation: CreateGroupLicenseOrderDocument,
              variables: {
                license_offering_id: orderData.license_offering_id,
                name: orderData.name,
                seat_count: orderData.seat_count,
                role: orderData.role,
                coupon_code: orderData.coupon_code,
                stripe_checkout_redirects: stripeCheckoutRedirects
              }
            })

          case OrderType.LifetimeSchoolSubscription:
            return apolloClient.mutate<
              CreateLifetimeSchoolSubscriptionOrderMutation,
              CreateLifetimeSchoolSubscriptionOrderMutationVariables
            >({
              mutation: CreateLifetimeSchoolSubscriptionOrderDocument,
              variables: {
                plan_id: orderData.plan_id,
                coupon_code: orderData.coupon_code,
                plan_visibility_code: orderData.plan_visibility_code,
                stripe_checkout_redirects: stripeCheckoutRedirects
              }
            })

          case OrderType.Registration:
            return apolloClient.mutate<
              CreateRegistrationOrderMutation,
              CreateRegistrationOrderMutationVariables
            >({
              mutation: CreateRegistrationOrderDocument,
              variables: {
                offering_id: orderData.offering_id,
                coupon_code: orderData.coupon_code,
                invitation_code: orderData.invitation_code,
                use_subscription_plan_id: orderData.use_subscription_plan_id,
                use_group_subscription_id: orderData.use_group_subscription_id,
                stripe_checkout_redirects: stripeCheckoutRedirects
              }
            })

          case OrderType.SchoolSubscription:
            return apolloClient.mutate<
              CreateSchoolSubscriptionOrderMutation,
              CreateSchoolSubscriptionOrderMutationVariables
            >({
              mutation: CreateSchoolSubscriptionOrderDocument,
              variables: {
                plan_id: orderData.plan_id,
                plan_visibility_code: orderData.plan_visibility_code,
                start_free_trial: orderData.start_free_trial,
                coupon_code: orderData.coupon_code,
                redirect_to: orderData.redirect_to,
                stripe_checkout_redirects: stripeCheckoutRedirects
              }
            })

          case OrderType.PlatformSubscription:
            return apolloClient.mutate<
              CreatePlatformSubscriptionOrderMutation,
              CreatePlatformSubscriptionOrderMutationVariables
            >({
              mutation: CreatePlatformSubscriptionOrderDocument,
              variables: {
                billing_plan_id: orderData.billing_plan_id,
                interval: orderData.interval,
                schoolId: orderData.schoolId,
                stripe_checkout_redirects: stripeCheckoutRedirects
              }
            })

          case OrderType.GroupSubscription:
            return apolloClient.mutate<
              CreateGroupSubscriptionOrderMutation,
              CreateGroupSubscriptionOrderMutationVariables
            >({
              mutation: CreateGroupSubscriptionOrderDocument,
              variables: {
                price_id: orderData.price_id,
                quantity: orderData.quantity,
                group_name: orderData.group_name,
                reactivate_group_subscription_id:
                  orderData.reactivate_group_subscription_id,
                redirect_to: orderData.redirect_to,
                stripe_checkout_redirects: stripeCheckoutRedirects
              }
            })

          default:
            exhaustiveGuard(orderType)
        }
      }
      const mutationResult = await getMutationResult()

      if (!mutationResult) {
        throw new Error("Order not recognized.")
      }

      const order = Object.values(mutationResult.data || {})[0] as OrderFragment

      if (!order) {
        throw new Error("Unable to process order.")
      }

      // Track this event in Plausible.
      // TODO: mv this specific tracking code elsewhere.
      if (orderType === OrderType.PlatformSubscription && order?.id) {
        try {
          // NOTE: not sure what orderData we should actually expect here, but the
          // values being used are not included in the type.
          savePendingActivateContext(order.id, {
            // @ts-ignore
            spaceId: orderData.schoolId,
            // @ts-ignore
            createdDate: orderData.createdDate,
            // @ts-ignore
            type: orderData.school_type
          })
        } catch (error) {
          console.error(error)
        }
      }

      setOrder(order)
      dispatchOrderEvent(ORDER_CREATED_EVENT, { user: me, order })
    } catch (error) {
      setError((error as any).message)
      throw error
    } finally {
      setLoading(false)
    }
  }, [
    orderType,
    orderData,
    me,
    savePendingActivateContext,
    stripeCheckoutRedirects
  ])

  return {
    order,
    handleOrder,
    loading,
    error,
    clearError
  }
}

// helper for handling order fulfillment after an order has been created
// either the user will be redirected to Stripe Checkout to fulfill the order
// via payment/coupon or, in the case of an order that does not require a transaction
// the order is automatically fulfilled and they will be redirected to the confirmation
export const useOrderFulfillment = (
  // options are not required
  options: {
    order: OrderFragment | null
    stripeApiKey?: string
  } = {
    order: null
  }
) => {
  // optionally controlling internal state
  const [order, setOrder] = useControlledState(options.order)
  const history = useHistory()

  // getting contextQuery in order to refetch after non-stripe transaction
  // (since Stripe Checkout will result in a browser refresh of Pathwright anyhow)
  const [loadContext] = useContextLazyQuery({
    fetchPolicy: "cache-and-network"
  })

  // Prop override wins; else the key on the order (processor / school that
  // created the Checkout Session). Do not use context.school — GraphQL returns
  // "" while anonymous and Apollo can keep that cached after signup.
  const stripeApiKey =
    options.stripeApiKey || order?.stripe_publishable_api_key || ""

  const handleOrderFulfillment = (order: OrderFragment) => {
    if (order.checkout_session_id) {
      if (!stripeApiKey) {
        return
      }
      // @ts-ignore
      window.Stripe(stripeApiKey).redirectToCheckout({
        sessionId: order.checkout_session_id
      })
    } else {
      // refetching context mainly in order to update the user's group_role_stats
      // which controls what Home UIs are available to the user
      loadContext()

      // If order doesn't return a Stripe checkout session we
      // can simply navigate to order confirmation screen.
      const confirmationUrl = new URL(order.order_data.success_url)
      // Set the order ID in the query params.
      confirmationUrl.searchParams.set("order_id", order.id.toString())
      history.push(getRelativeUrl(confirmationUrl.href))
    }
  }

  useEffect(() => {
    if (order) handleOrderFulfillment(order)
  }, [order, stripeApiKey])

  // return an optional setter in case order is not passed in as a prop
  return setOrder
}

function useIsAwaitingCheckout({
  order,
  loading,
  error
}: {
  order: OrderFragment | null
  loading: boolean
  error: string | null
}) {
  // We're awaiting Stripe checkout to open when loading the order, or the order exists
  // as long as there's no error.
  const isAwaitingCheckout = useMemo(() => {
    return !!(order || loading) && !error
  }, [order, loading, error])

  return isAwaitingCheckout
}

// Somewhat streamlined version of the the <OrderStripeCheckoutForm /> where
// the expectation is that the order will result in a checkout session,
// and consumers can render a button as seen fit in the calling component.
export function useStripeOrder({
  orderType,
  orderData,
  stripeApiKey: stripeApiKeyProp
}: {
  stripeApiKey?: string | null
} & UseOrderStripeCheckoutProps) {
  const { order, handleOrder, loading, error, clearError } =
    useOrderStripeCheckout({
      orderType,
      orderData
    } as UseOrderStripeCheckoutProps)

  const stripeApiKey =
    stripeApiKeyProp || order?.stripe_publishable_api_key || ""

  usePendingPlatformOrderPolling(orderType === OrderType.PlatformSubscription)

  const isAwaitingCheckout = useIsAwaitingCheckout({
    order,
    loading,
    error
  })

  useEffect(() => {
    if (!order?.checkout_session_id) return
    if (!stripeApiKey) return
    // @ts-ignore
    window.Stripe(stripeApiKey).redirectToCheckout({
      sessionId: order.checkout_session_id
    })
  }, [order, stripeApiKey])

  return {
    order,
    handleOrder,
    loading: isAwaitingCheckout,
    error,
    clearError
  }
}

type OrderStripeCheckoutFormProps = {
  disabled?: boolean
  stripeApiKey?: string
  renderButton?: (props: {
    disabled?: boolean
    order: OrderFragment | null
    isAwaitingCheckout: boolean
    onSubmit: () => void
    error: string | null
    clearError: () => void
    submitLabel: string
    loadingText: string
  }) => React.ReactNode
  maintenanceModeOverride?: boolean // for testing purposes
  submitLabel?: string
  attemptToConvert?: boolean
  // A function that will be called before the order is submitted. Will be passed the order handler.
  handleSubmitOrder?: (handleOrder: () => Promise<void>) => Promise<void> | void
} & UseOrderStripeCheckoutProps

const OrderStripeCheckoutForm = function OrderStripeCheckoutForm({
  disabled,
  orderType,
  orderData,
  stripeApiKey,
  renderButton,
  maintenanceModeOverride = false,
  stripeCheckoutRedirects,
  attemptToConvert = false,
  handleSubmitOrder,
  ...rest
}: OrderStripeCheckoutFormProps) {
  const colorPalette = useColorModeValue("brand", undefined)
  const { t } = useTranslate()
  const { platformConfig, me } = usePathwrightContext()

  const submitLabel =
    rest.submitLabel || t("order.checkout.submit.Continue to Checkout")

  const { order, handleOrder, loading, error, clearError } =
    useOrderStripeCheckout({
      orderType,
      orderData,
      stripeCheckoutRedirects
    } as UseOrderStripeCheckoutProps)

  const handleSubmit = useCallback(() => {
    return handleSubmitOrder ? handleSubmitOrder(handleOrder) : handleOrder()
  }, [handleSubmitOrder, handleOrder])

  usePendingPlatformOrderPolling(orderType === OrderType.PlatformSubscription)

  const isAwaitingCheckout = useIsAwaitingCheckout({
    order,
    loading,
    error
  })

  // Stripe.js redirect uses the key on the created order (or stripeApiKey override).
  useOrderFulfillment({ order, stripeApiKey })

  useEffect(() => {
    // Cover auto-convert mounts this form as soon as signup completes.
    // Wait for `me` so the order mutation is authenticated — otherwise
    // Django often returns 404 Not Found under load.
    if (attemptToConvert && me?.id) {
      handleSubmit()
    }
  }, [attemptToConvert, me?.id])

  if (platformConfig.payments_maintenance_mode && !maintenanceModeOverride) {
    return (
      <CenteredContent>
        <BlankSlate
          icon="caution-triangle"
          heading="Payments Under Maintenance."
          body={
            <span>
              Please{" "}
              <a href="https://status.pathwright.com/" target="_blank">
                check back
              </a>{" "}
              shortly to complete your purchase.
            </span>
          }
        />
      </CenteredContent>
    )
  }

  if (renderButton) {
    return (
      <React.Fragment>
        {renderButton({
          disabled: disabled || isAwaitingCheckout,
          order,
          isAwaitingCheckout,
          onSubmit: handleSubmit,
          error,
          clearError,
          submitLabel,
          loadingText: submitLabel
        })}
        <Alert error={error} onClear={clearError} />
      </React.Fragment>
    )
  }

  return (
    <CenteredContent>
      <Button
        variant="solid"
        size="lg"
        colorPalette={colorPalette}
        type="submit"
        {...rest}
        onClick={handleSubmit}
        loading={isAwaitingCheckout}
        disabled={disabled || isAwaitingCheckout}
        loadingText={submitLabel}
      >
        {submitLabel}
      </Button>
      <Alert error={error} onClear={clearError} />
    </CenteredContent>
  )
}

OrderStripeCheckoutForm.displayName = "OrderStripeCheckoutForm"

export default OrderStripeCheckoutForm
